import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from '@material-ui/core'
import { FormGroup } from '../form/FormGroup'
import { Dialog } from '../dialog/Dialog'
import {
  CommentFragment,
  CommentLocationType,
  ProjectFragment,
} from '../../generated/graphql'
import { useComments } from '../../comments/use-comments'
import { useAuthorization } from '../../authorization/use-authorization'
import { createSortByProperty } from '../../util/array-utility'

type Props = {
  project: ProjectFragment
  locationType: CommentLocationType
  isOpen: boolean
  onClose: () => void
}

export const commentIsInLocation =
  (locationType: CommentLocationType) =>
  (comment: CommentFragment): boolean =>
    comment.locationType === locationType

export function CommentsDialog({
  project,
  locationType,
  isOpen,
  onClose,
}: Props): React.ReactElement {
  const projectComment = project.comments.find(
    commentIsInLocation(locationType),
  )
  const versionComments = project.versions
    .map((version) => ({
      versionId: version.id,
      versionName: version.name,
      comment: version.comments.find(commentIsInLocation(locationType)),
    }))
    .sort(createSortByProperty('versionName'))

  const defaultProjectText = projectComment?.text ?? ''
  const defaultVersionIdsToText = versionComments.reduce(
    (records, { versionId, comment }) => ({
      ...records,
      [versionId]: comment?.text ?? '',
    }),
    {} as Record<string, string | undefined>,
  )

  const [projectText, setProjectText] = useState(defaultProjectText)
  const [versionIdsToText, setVersionIdsToText] = useState(
    defaultVersionIdsToText,
  )
  const { t } = useTranslation()
  const { createComment, updateComment, deleteComment, isLoading } =
    useComments()
  const { canEditProject } = useAuthorization()

  const handleCommentModification = (
    text: string,
    comment?: CommentFragment,
    projectId?: string,
    versionId?: string,
    // eslint-disable-next-line consistent-return
  ) => {
    if (comment) {
      if (text.length === 0) {
        return deleteComment(comment.id, projectId, versionId)
      }
      if (text === comment.text) {
        // eslint-disable-next-line consistent-return
        return
      }
      const input = { text }
      return updateComment(comment.id, input)
    }
    if (text.length > 0) {
      const input = { locationType, text }
      return createComment(input, projectId, versionId)
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const promises = [
      handleCommentModification(projectText, projectComment, project.id),
    ]

    const versionPromises = versionComments.map(({ versionId, comment }) =>
      handleCommentModification(
        versionIdsToText[versionId] ?? '',
        comment,
        undefined,
        versionId,
      ),
    )
    promises.push(...versionPromises)

    await Promise.all(promises)
    onClose()
  }

  const handleOnClose = () => {
    setProjectText(defaultProjectText)
    setVersionIdsToText(defaultVersionIdsToText)
    onClose()
  }

  return (
    <Dialog
      title={t(`comments.locationType.${locationType}`)}
      open={isOpen}
      onClose={handleOnClose}
    >
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <FormGroup>
            <TextField
              name="text"
              type="text"
              label={t('comments.projectSpecific')}
              value={projectText}
              onChange={(event) => setProjectText(event.target.value)}
              multiline
            />
          </FormGroup>

          {versionComments.map(({ versionId, versionName }) => (
            <FormGroup key={versionId}>
              <TextField
                name="text"
                type="text"
                label={t('comments.versionSpecific', { versionName })}
                value={versionIdsToText[versionId]}
                onChange={(event) =>
                  setVersionIdsToText({
                    ...versionIdsToText,
                    [versionId]: event.target.value,
                  })
                }
                multiline
              />
            </FormGroup>
          ))}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleOnClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            data-cy="create-comment-button"
            disabled={!canEditProject(project) || isLoading}
          >
            {t('common.saveChanges')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
